import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MailCheck, MailWarning } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import LogoSwipRent from "@/components/brand/logo_swiprent";
import ButtonMain from "@/components/buttons/button_main";

// Landing page for the emailed verification link:
// https://swiprent.com/auth/verify-email?token=...
export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [state, setState] = useState("working"); // working | done | error
  const [message, setMessage] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = searchParams.get("token");
    if (!token) {
      setState("error");
      setMessage("This verification link is missing its token. Open the exact link from your email.");
      return;
    }
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        await refresh();
        setState("done");
      } catch (error) {
        setState("error");
        setMessage(formatApiError(error));
      }
    })();
  }, [searchParams, refresh]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-day px-4 py-10">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="w-full max-w-md rounded-3xl border border-slate-100 bg-white p-8 text-center shadow-sm"
      >
        <Link to="/" className="inline-block">
          <LogoSwipRent />
        </Link>

        {state === "working" && (
          <>
            <div className="mx-auto mt-8 h-10 w-10 animate-spin rounded-full border-[3px] border-brand-line border-t-brand-lime" />
            <p className="mt-5 text-sm font-semibold text-slate-500">Confirming your email...</p>
          </>
        )}

        {state === "done" && (
          <>
            <span className="mx-auto mt-8 flex h-14 w-14 items-center justify-center rounded-full bg-brand-tint text-brand-lime">
              <MailCheck size={26} strokeWidth={2} />
            </span>
            <h1 className="mt-5 text-2xl font-extrabold tracking-tight text-brand-ink">Email verified</h1>
            <p className="mt-2 text-sm font-semibold text-slate-500">
              You are all set. Swiping, saving, listing and chat are now unlocked.
            </p>
            <ButtonMain full className="mt-6" onClick={() => navigate(user ? "/swipe" : "/auth/login")}>
              {user ? "Start swiping" : "Log in"}
            </ButtonMain>
          </>
        )}

        {state === "error" && (
          <>
            <span className="mx-auto mt-8 flex h-14 w-14 items-center justify-center rounded-full bg-amber-50 text-amber-600">
              <MailWarning size={26} strokeWidth={2} />
            </span>
            <h1 className="mt-5 text-2xl font-extrabold tracking-tight text-brand-ink">That link did not work</h1>
            <p className="mt-2 text-sm font-semibold text-slate-500">{message}</p>
            <ButtonMain full className="mt-6" onClick={() => navigate(user ? "/profile" : "/auth/login")}>
              {user ? "Go to my profile" : "Log in"}
            </ButtonMain>
          </>
        )}
      </motion.div>
    </div>
  );
}
